import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../shared/confirmdialog/confirmdialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentType } from '@angular/cdk/portal';
import { ErrorTranslations } from '../models/forms.model';
import { Utils } from '../shared/utils';
import { isArray } from 'lodash';
import { ErrorLabel } from '../models/error-label.model';
import { TranslationService } from './translation.service';

@Injectable({ providedIn: 'root' })
export class DialogService {
  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translate: TranslationService
  ) { }

  popError(message: string, ...params: string[]) {
    this.popErrorsCore([message], params);
  }

  popWarning(message: string, ...params: string[]) {
    this.popWarningsCore([message], params);
  }

  popWarningCustom(message: string, duration: number, ...params: string[]) {
    this.popWarningsCore([message], params, duration);
  }

  popErrors(labels: string[], duration: number = 2000) {
    this.popErrorsCore(labels, undefined, duration);
  }

  popApiError(error: any, messagePrefix: string | undefined = undefined) {
    if (error.error && error.status !== 404) {
      if (error.error.ErrorMessages && error.error.ErrorMessages.length > 0) {
        this.popErrors(error.error.ErrorMessages, 5000);
      } else if (error.error.ErrorLabels && error.error.ErrorLabels.length > 0) {
        const errors = error.error.ErrorLabels.map((er: ErrorLabel) =>
          this.translate.translateErrorLabel(er)
        );
        this.popErrors(errors, 5000);
      } else {
        let message = error.error.ErrorCode;
        if (!message) {
          if (error.error.title) {
            message = error.error.title;
          } else {
            message = error.message;
          }
        }
        if (error.error.ErrorMessage) {
          const authError = this.tryHandleAuthenticationErrors(
            error.error.ErrorMessage
          );
          if (authError) {
            message = authError;
          } else {
            message += ' - ' + error.error.ErrorMessage;
          }
        }
        if (messagePrefix) {
          message = messagePrefix + ' - ' + message;
        }
        this.popErrors([message], 5000);
      }
    } else if (error.status !== 404) {
      this.popErrors([error.message], 5000);
    }
  }

  private tryHandleAuthenticationErrors(errorMessage: string): string | undefined {
    try {
      const parsed = JSON.parse(errorMessage);
      if (parsed && isArray(parsed)) {
        const code = parsed[0].code;
        if (code === 'DuplicateEmail' || code === 'DuplicateUserName') {
          return 'register.form.errors.emailTaken';
        }
      }
    } catch { }
    return undefined;
  }

  popErrorTranslation(
    errorsTranslations: ErrorTranslations[],
    langIso: string,
    errorCode: string,
    ...params: string[]
  ) {
    const errorMessage = Utils.createErrorTranslationMssageWithParameters(
      errorsTranslations,
      langIso,
      errorCode,
      params
    );
    this.popError(errorMessage);
  }

  private popErrorsCore(
    labels: string | string[],
    params?: string[],
    duration: number = 2000
  ) {
    if (!Array.isArray(labels)) {
      labels = [labels];
    }
    this.popMessageCore(labels, params, duration, ['error-snack-bar']);
  }

  private popWarningsCore(
    labels: string[],
    params?: string[],
    duration: number = 2000
  ) {
    this.popMessageCore(labels, params, duration, ['warning-snack-bar']);
  }

  private popMessageCore(
    labels: string[],
    params?: string[],
    duration: number = 2000,
    panelClass?: string[]
  ) {
    let message = labels
      .map((i: string) => this.translate.translate(i))
      .join('\r\n');

    if (params) {
      for (let i = 0; i < params.length; i++) {
        message = message.replace('{' + i + '}', params[i]);
      }
    }

    if (message.length > 1000) {
      message = message.substring(0, 1000);
    }

    this.snackBar.open(message, '', {
      duration,
      panelClass,
    });
  }

  popSuccess(
    message: string,
    duration: number = 2000,
    panelClass: string[] = ['success-snack-bar']
  ) {
    this.snackBar.open(message, '', {
      duration,
      panelClass,
    });
  }

  confirmDialog(data: {
    title?: string;
    message?: string;
    cancelButtonText?: string;
    confirmButtonText?: string;
  }): Observable<boolean> {
    const dialogConfig: MatDialogConfig = {
      data,
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    return dialogRef.afterClosed();
  }

  openDialog<T, D = any, R = any>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    return this.dialog.open(component, config);
  }
}
